import { ChatResponse } from 'src/app/models/chat.model';
import { MS_PER_DAY, MS_PER_HOUR, MS_PER_MINUTE } from 'src/app/shared/constants/chat.constants';

const PREVIOUS_DAYS_WINDOW = 7;

export type ChatRailGroupName = 'Starred' | 'Today' | 'Yesterday' | 'Previous 7 days' | 'Older';

export type ChatRailTimeLabel =
  | { type: 'translated'; key: string; params?: { minutes?: number; hours?: number; days?: number } }
  | { type: 'absolute'; date: string };

export interface ChatRailRow {
  chat: ChatResponse;
  timeLabel: ChatRailTimeLabel;
}

export interface ChatRailGroup {
  name: ChatRailGroupName;
  rows: ChatRailRow[];
}

const GROUP_ORDER: ChatRailGroupName[] = ['Starred', 'Today', 'Yesterday', 'Previous 7 days', 'Older'];

export function groupChatsForRail(chats: ChatResponse[], now: Date): ChatRailGroup[] {
  const rowsByGroup = new Map<ChatRailGroupName, ChatRailRow[]>();

  for (const chat of chats) {
    const updatedAt = new Date(chat.updatedAt);
    const groupName = getGroupName(chat, now, updatedAt);
    const row: ChatRailRow = { chat, timeLabel: getTimeLabel(groupName, now, updatedAt) };

    const rows = rowsByGroup.get(groupName);
    if (rows) {
      rows.push(row);
    } else {
      rowsByGroup.set(groupName, [row]);
    }
  }

  return GROUP_ORDER.filter((name) => rowsByGroup.has(name)).map((name) => ({
    name,
    rows: rowsByGroup.get(name) as ChatRailRow[],
  }));
}

function getGroupName(chat: ChatResponse, now: Date, updatedAt: Date): ChatRailGroupName {
  if (chat.isStarred) {
    return 'Starred';
  }

  const dayDiff = getCalendarDayDiff(now, updatedAt);
  if (dayDiff === 0) {
    return 'Today';
  }
  if (dayDiff === 1) {
    return 'Yesterday';
  }
  if (dayDiff >= 2 && dayDiff <= PREVIOUS_DAYS_WINDOW) {
    return 'Previous 7 days';
  }
  return 'Older';
}

function getTimeLabel(groupName: ChatRailGroupName, now: Date, updatedAt: Date): ChatRailTimeLabel {
  if (groupName === 'Starred') {
    return { type: 'translated', key: 'Starred' };
  }
  if (groupName === 'Yesterday') {
    return { type: 'translated', key: 'Yesterday' };
  }
  if (groupName === 'Previous 7 days') {
    return { type: 'translated', key: '{{days}} days ago', params: { days: getCalendarDayDiff(now, updatedAt) } };
  }
  if (groupName === 'Older') {
    return { type: 'absolute', date: updatedAt.toISOString() };
  }

  const diffMinutes = Math.floor((now.getTime() - updatedAt.getTime()) / MS_PER_MINUTE);
  if (diffMinutes < 1) {
    return { type: 'translated', key: 'Just now' };
  }
  if (diffMinutes < 60) {
    return { type: 'translated', key: '{{minutes}}m ago', params: { minutes: diffMinutes } };
  }
  const diffHours = Math.floor(diffMinutes / 60);
  return { type: 'translated', key: '{{hours}}h ago', params: { hours: diffHours } };
}

function getCalendarDayDiff(now: Date, date: Date): number {
  const startOfNow = startOfDay(now).getTime();
  const startOfDate = startOfDay(date).getTime();
  return Math.round((startOfNow - startOfDate) / MS_PER_DAY);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
